import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient as api } from '@plerionhq/design-system';

/**
 * Integration groups (MI-210). Groups are derived from cloud account tags: an
 * organization admin selects tag keys, and Plerion creates one group per key
 * and value found across the tenant's integrations. Nothing here creates or
 * deletes a group directly; saving the key selection does both.
 */
export type IntegrationGroupSource = {
  kind: 'tag';
  key: string;
  value: string;
};

export type IntegrationGroup = {
  groupId: string;
  /** Derived from the source, e.g. `Division:Payment`. */
  name: string;
  source: IntegrationGroupSource;
  memberIntegrationIds: string[];
  computedAt?: string;
  /** Groups carry only these two states; tenant-model's 'failed' lands on the config item. */
  status?: 'computed' | 'orphaned';
};

const GROUPS_KEY = ['api', 'integration-groups'];
const GROUP_CONFIG_KEY = ['api', 'integration-group-config'];

/**
 * The tenant's derived groups. Pass `enabled: false` while the caller is
 * behind the integration-groups flag so no request is made at all.
 *
 * `refetchInterval` passes through to the query so a caller that just saved
 * the tag settings can poll until the recompute's output appears.
 */
export function useIntegrationGroups({
  enabled = true,
  refetchInterval,
}: {
  enabled?: boolean;
  refetchInterval?: (groups: IntegrationGroup[] | undefined) => number | false;
} = {}) {
  return useQuery({
    queryKey: GROUPS_KEY,
    enabled,
    refetchInterval: refetchInterval
      ? (query) => refetchInterval(query.state.data)
      : undefined,
    queryFn: async (): Promise<IntegrationGroup[]> => {
      const res = await api.get<{ data: IntegrationGroup[] }>(
        '/api/core/tenant/integration-groups',
      );
      return res.data.data ?? [];
    },
  });
}

/** The tag keys that govern grouping. Organization admins only. */
export function useIntegrationGroupConfig({
  enabled = true,
}: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: GROUP_CONFIG_KEY,
    enabled,
    queryFn: async (): Promise<string[]> => {
      const res = await api.get<{ data: { tagKeys: string[] } }>(
        '/api/core/tenant/integration-groups/config',
      );
      return res.data.data?.tagKeys ?? [];
    },
  });
}

export function useUpdateIntegrationGroupConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tagKeys: string[]): Promise<string[]> => {
      const res = await api.put<{ data: { tagKeys: string[] } }>(
        '/api/core/tenant/integration-groups/config',
        { tagKeys },
      );
      return res.data.data?.tagKeys ?? [];
    },
    onSuccess: (tagKeys) => {
      // Write the server echo straight into the cache: invalidating instead
      // would leave a window where the config query still holds the old keys
      // and a caller that cleared its draft flickers back to them.
      queryClient.setQueryData(GROUP_CONFIG_KEY, tagKeys);
      // The groups themselves only change once the recompute has run, but
      // refetching keeps the list honest if it already has.
      queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    },
  });
}
